// settings & const
const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
};

/**
 * Create an openapi-fetch client.
 * @type {import("./index.js").default}
 */
function createClient(clientOptions) {
  const {
    fetch: baseFetch = globalThis.fetch,
    querySerializer: globalQuerySerializer,
    bodySerializer: globalBodySerializer,
    ...baseOptions
  } = clientOptions ?? {};
  let baseUrl = baseOptions.baseUrl ?? "";
  if (baseUrl.endsWith("/")) {
    baseUrl = baseUrl.slice(0, -1); // remove trailing slash
  }

  /**
   * Per-request fetch (keeps settings created in createClient()
   * @param {string} url
   * @param {import('./index.js').FetchOptions} fetchOptions
   */
  async function coreFetch(url, fetchOptions) {
    const {
      fetch = baseFetch,
      headers,
      body: requestBody,
      params = {},
      parseAs = "json",
      querySerializer = globalQuerySerializer ?? defaultQuerySerializer,
      bodySerializer = globalBodySerializer ?? defaultBodySerializer,
      ...init
    } = fetchOptions || {};

    // URL
    const finalURL = createFinalURL(url, {
      baseUrl,
      params,
      querySerializer,
    });
    const finalHeaders = mergeHeaders(
      DEFAULT_HEADERS,
      clientOptions?.headers,
      headers,
      params.header,
    );

    // fetch!
    const requestInit = {
      redirect: "follow",
      ...baseOptions,
      ...init,
      headers: finalHeaders,
    };

    if (requestBody) {
      requestInit.body = bodySerializer(requestBody);
    }
    // remove `Content-Type` if serialized body is FormData; browser will correctly set Content-Type & boundary expression
    if (requestInit.body instanceof FormData) {
      finalHeaders.delete("Content-Type");
    }

    const response = await fetch(finalURL, requestInit);

    // handle empty content
    // note: we return `{}` because we want user truthy checks for `.data` or `.error` to succeed
    if (
      response.status === 204 ||
      response.headers.get("Content-Length") === "0"
    ) {
      return response.ok ? { data: {}, response } : { error: {}, response };
    }

    // parse response (falling back to .text() when necessary)
    if (response.ok) {
      // if "stream", skip parsing entirely
      if (parseAs === "stream") {
        // fix for bun: bun consumes response.body, therefore clone before accessing
        // TODO: test this?
        return { data: response.clone().body, response };
      }
      const cloned = response.clone();
      return {
        data:
          typeof cloned[parseAs] === "function"
            ? await cloned[parseAs]()
            : await cloned.text(),
        response,
      };
    }

    // handle errors (always parse as .json() or .text())
    let error = {};
    try {
      error = await response.clone().json();
    } catch {
      error = await response.clone().text();
    }
    return { error, response };
  }

  return {
    /** Call a GET endpoint */
    async GET(url, init) {
      return coreFetch(url, { ...init, method: "GET" });
    },
    /** Call a PUT endpoint */
    async PUT(url, init) {
      return coreFetch(url, { ...init, method: "PUT" });
    },
    /** Call a POST endpoint */
    async POST(url, init) {
      return coreFetch(url, { ...init, method: "POST" });
    },
    /** Call a DELETE endpoint */
    async DELETE(url, init) {
      return coreFetch(url, { ...init, method: "DELETE" });
    },
    /** Call a OPTIONS endpoint */
    async OPTIONS(url, init) {
      return coreFetch(url, { ...init, method: "OPTIONS" });
    },
    /** Call a HEAD endpoint */
    async HEAD(url, init) {
      return coreFetch(url, { ...init, method: "HEAD" });
    },
    /** Call a PATCH endpoint */
    async PATCH(url, init) {
      return coreFetch(url, { ...init, method: "PATCH" });
    },
    /** Call a TRACE endpoint */
    async TRACE(url, init) {
      return coreFetch(url, { ...init, method: "TRACE" });
    },
  };
}

// utils

/**
 * Serialize query params to string
 * @type {import("./index.js").defaultQuerySerializer}
 */
function defaultQuerySerializer(q) {
  const search = [];
  if (q && typeof q === "object") {
    for (const [k, v] of Object.entries(q)) {
      const value = defaultQueryParamSerializer([k], v);
      if (value) {
        search.push(value);
      }
    }
  }
  return search.join("&");
}

/**
 * Serialize query param schema types according to expected default OpenAPI 3.x behavior
 * @type {import("./index.js").defaultQueryParamSerializer}
 */
function defaultQueryParamSerializer(key, value) {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === "string") {
    return `${deepObjectPath(key)}=${encodeURIComponent(value)}`;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return `${deepObjectPath(key)}=${String(value)}`;
  }
  if (Array.isArray(value)) {
    if (!value.length) {
      return undefined;
    }
    const nextValue = [];
    for (const item of value) {
      const next = defaultQueryParamSerializer(key, item);
      if (next !== undefined) {
        nextValue.push(next);
      }
    }
    return nextValue.join(`&`);
  }
  if (typeof value === "object") {
    if (!Object.keys(value).length) {
      return undefined;
    }
    const nextValue = [];
    for (const [k, v] of Object.entries(value)) {
      if (v !== undefined && v !== null) {
        const next = defaultQueryParamSerializer([...key, k], v);
        if (next !== undefined) {
          nextValue.push(next);
        }
      }
    }
    return nextValue.join("&");
  }
  return encodeURIComponent(`${deepObjectPath(key)}=${String(value)}`);
}

/**
 * Flatten a node path into a deepObject string
 * @type {import("./index.js").deepObjectPath}
 */
function deepObjectPath(path) {
  let output = path[0];
  for (const k of path.slice(1)) {
    output += `[${k}]`;
  }
  return output;
}

/**
 * Serialize body object to string
 * @type {import("./index.js").defaultBodySerializer}
 */
function defaultBodySerializer(body) {
  return JSON.stringify(body);
}

/**
 * Construct URL string from baseUrl and handle path and query params
 * @type {import("./index.js").createFinalURL}
 */
function createFinalURL(pathname, options) {
  let finalURL = `${options.baseUrl}${pathname}`;
  if (options.params.path) {
    for (const [k, v] of Object.entries(options.params.path)) {
      finalURL = finalURL.replace(`{${k}}`, encodeURIComponent(String(v)));
    }
  }
  const search = options.querySerializer(options.params.query ?? {});
  if (search) {
    finalURL += `?${search}`;
  }
  return finalURL;
}

/**
 * Merge headers a and b, with b taking priority
 * @type {import("./index.js").mergeHeaders}
 */
function mergeHeaders(...allHeaders) {
  const headers = new Headers();
  for (const headerSet of allHeaders) {
    if (!headerSet || typeof headerSet !== "object") {
      continue;
    }
    const iterator =
      headerSet instanceof Headers
        ? headerSet.entries()
        : Object.entries(headerSet);
    for (const [k, v] of iterator) {
      if (v === null) {
        headers.delete(k);
      } else if (v !== undefined) {
        headers.set(k, v);
      }
    }
  }
  return headers;
}

const fieldEmployeeScopes = [
    'submit-repair-request',
    'view-repair-status',
    'view-parts-status',
    'submit-time-entry',
];
const fieldSupervisorScopes = [
    'submit-repair-request',
    'submit-parts-request',
    'view-repair-status',
    'view-parts-status',
    'submit-time-entry',
    'view-others-time-entry',
    'approve-time-entry',
    'assign-repair-tickets-to-self',
];
const mechanicScopes = [
    'submit-repair-request',
    'submit-parts-request',
    'view-repair-status',
    'view-parts-status',
    'submit-time-entry',
    'assign-repair-tickets-to-self',
];
const payrollAdminScopes = [
    'submit-time-entry',
    'view-others-time-entry',
    'download-payroll-reports',
    'override-approved-time-entry',
    'create-labor-codes',
    'create-new-user',
    'update-other-user',
    'disable-user',
    'manage-roles',
];
const partsTeamScopes = [
    'approve-parts-request',
    'order-parts',
];
const repairAdminScopes = [
    'submit-repair-request',
    'view-repair-status',
    'assign-repair-tickets-to-self',
    'assign-repair-tickets-to-others',
    'approve-repair-request',
];
const truckingAdminScopes = [
    'submit-trucking-request',
    'truck-dispatching',
];
const featureCategories = [
    'Repair',
    'Parts',
    'Time',
    'Manage',
    'Users',
    'Truck',
    'Schedule',
];
const repairFeatureScopes = [
    'submit-repair-request',
    'view-repair-status',
    'assign-repair-tickets-to-self',
    'assign-repair-tickets-to-others',
    'approve-repair-request',
];
const partsFeatureScopes = [
    'view-parts-status',
    'submit-parts-request',
    'approve-parts-request',
    'order-parts',
];
const timeFeatureScopes = [
    'submit-time-entry',
    'view-others-time-entry',
    'approve-time-entry',
    'override-approved-time-entry',
    'download-payroll-reports',
    'create-labor-codes',
];
const manageFeatureScopes = ['create-labor-codes'];
const usersFeatureScopes = [
    'create-new-user',
    'update-other-user',
    'disable-user',
    'manage-roles',
];
const truckingFeatureScopes = [
    'submit-trucking-request',
    'truck-dispatching',
];
// These are used to determine which sections of the app are available to a given user
const featureCategoryScopes = {
    Repair: repairFeatureScopes,
    Parts: partsFeatureScopes,
    Time: timeFeatureScopes,
    Manage: manageFeatureScopes,
    Users: usersFeatureScopes,
    Truck: truckingFeatureScopes,
    Schedule: truckingFeatureScopes,
};
const allRoles = [
    'FieldEmployee',
    'FieldSupervisor',
    'Mechanic',
    'PayrollAdmin',
    'PartsTeam',
    'RepairAdmin',
    'TruckingAdmin',
];
const roleScopes = {
    FieldEmployee: fieldEmployeeScopes,
    FieldSupervisor: fieldSupervisorScopes,
    Mechanic: mechanicScopes,
    PayrollAdmin: payrollAdminScopes,
    PartsTeam: partsTeamScopes,
    RepairAdmin: repairAdminScopes,
    TruckingAdmin: truckingAdminScopes,
};

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isPrimitive = (value) => value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'undefined';

class InvalidTokenError extends Error {
}
InvalidTokenError.prototype.name = "InvalidTokenError";
function b64DecodeUnicode(str) {
    return decodeURIComponent(atob(str).replace(/(.)/g, (m, p) => {
        let code = p.charCodeAt(0).toString(16).toUpperCase();
        if (code.length < 2) {
            code = "0" + code;
        }
        return "%" + code;
    }));
}
function base64UrlDecode(str) {
    let output = str.replace(/-/g, "+").replace(/_/g, "/");
    switch (output.length % 4) {
        case 0:
            break;
        case 2:
            output += "==";
            break;
        case 3:
            output += "=";
            break;
        default:
            throw new Error("base64 string is not of the correct length");
    }
    try {
        return b64DecodeUnicode(output);
    }
    catch (err) {
        return atob(output);
    }
}
function jwtDecode(token, options) {
    if (typeof token !== "string") {
        throw new InvalidTokenError("Invalid token specified: must be a string");
    }
    options || (options = {});
    const pos = options.header === true ? 0 : 1;
    const part = token.split(".")[pos];
    if (typeof part !== "string") {
        throw new InvalidTokenError(`Invalid token specified: missing part #${pos + 1}`);
    }
    let decoded;
    try {
        decoded = base64UrlDecode(part);
    }
    catch (e) {
        throw new InvalidTokenError(`Invalid token specified: invalid base64 for part #${pos + 1} (${e.message})`);
    }
    try {
        return JSON.parse(decoded);
    }
    catch (e) {
        throw new InvalidTokenError(`Invalid token specified: invalid json for part #${pos + 1} (${e.message})`);
    }
}

const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
};

const isValidPassword = (password) => {
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z])(?=.*[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]).{6,}$/;
    return passwordRegex.test(password);
};

class Auth {
    // private static parent: DB;
    static _user = null;
    static _token = null;
    static _refreshToken = null;
    static _rememberMe = false;
    static onAuthStateChanged;
    static async init(onAuthStateChanged) {
        Auth.onAuthStateChanged = onAuthStateChanged;
        await Auth.populateFromCache();
    }
    /// -----------------------------------------------------------------
    /// GETTERS
    /// -----------------------------------------------------------------
    static get isInitialized() {
        return !!Auth.onAuthStateChanged;
    }
    static async getCachedValue(key) {
        const onHandKey = ('_' + key);
        const onHandValue = Auth[onHandKey];
        if (onHandValue !== null) {
            return onHandValue;
        }
        let cachedValue = await DB.localStorage.getItem(key);
        if (cachedValue) {
            if (key === 'user') {
                cachedValue = JSON.parse(cachedValue);
            }
            // @ts-ignore
            Auth[onHandKey] = cachedValue;
            return cachedValue;
        }
        return null;
    }
    static async getToken() {
        return await Auth.getCachedValue('token');
    }
    static async getRefreshToken() {
        return await Auth.getCachedValue('refreshToken');
    }
    static async getUser() {
        return await Auth.getCachedValue('user');
    }
    static async getRememberMe() {
        return await Auth.getCachedValue('rememberMe');
    }
    static async getDecodedToken(token) {
        try {
            // if the token was passed, decode and return it
            if (token) {
                return jwtDecode(token);
            }
            // if not passed, try to get it from cache and decode
            const cachedToken = await Auth.getToken();
            if (cachedToken) {
                return jwtDecode(cachedToken);
            }
        }
        catch (e) {
            throw new Error('Invalid token specified');
        }
        // if not on-hand or in localstorage, return null
        return null;
    }
    static async getUserPermissionsData(token) {
        if (!token || typeof token === 'string') {
            const decodedTokenData = await Auth.getDecodedToken(token);
            if (!decodedTokenData) {
                throw new Error('no token to extract permission data from');
            }
            token = decodedTokenData;
        }
        const roles = typeof token.roles === 'string' ? [token.roles] : token.roles;
        const scopes = typeof token.scope === 'string' ? [token.scope] : token.scope;
        return { roles, scopes };
    }
    /// -----------------------------------------------------------------
    /// SETTERS
    /// -----------------------------------------------------------------
    static async setCachedValue(key, value) {
        const onHandKey = ('_' + key);
        // @ts-ignore
        Auth[onHandKey] = value;
        if (key === 'token') {
            Auth.onAuthStateChanged(value);
        }
        if (value !== null) {
            if (key === 'user') {
                // @ts-expect-error - it doesn't like value being a string if key is user, since it should be passed as AuthenticatedUser
                value = JSON.stringify(value);
            }
            await DB.localStorage.setItem(key, String(value));
        }
        else {
            await DB.localStorage.removeItem(key);
        }
    }
    static async setRememberMe(value) {
        await Auth.setCachedValue('rememberMe', value);
    }
    static async setToken(value) {
        await Auth.setCachedValue('token', value);
    }
    static async setRefreshToken(value) {
        await Auth.setCachedValue('refreshToken', value);
    }
    static async setUser(value) {
        await Auth.setCachedValue('user', value);
    }
    /// -----------------------------------------------------------------
    /// Lifecycle utilities
    /// -----------------------------------------------------------------
    static async populateFromCache() {
        const token = await Auth.getToken();
        /// Populate the static variables from cache if possible
        if (token) {
            Auth._token = token;
            const refreshToken = await Auth.getRefreshToken();
            if (refreshToken) {
                Auth._refreshToken = refreshToken;
            }
            const userString = await Auth.getUser();
            if (userString) {
                Auth._user = userString;
            }
        }
        // Make sure the headers are in place
        Auth.onAuthStateChanged(token);
    }
    static async fetchTokenData(employeeNumber, password) {
        /// Get a token and refresh token from the server
        const { data: tokenData, error: authError } = await DB.POST('/auth/token', {
            body: {
                employeeNumber,
                password,
            },
        });
        if (authError) {
            throw new Error('Unauthorized');
        }
        await Auth.setToken(tokenData.token);
        await Auth.setRefreshToken(tokenData.refreshToken);
        return tokenData;
    }
    /// -----------------------------------------------------------------
    /// AUTH FUNCTIONS
    /// -----------------------------------------------------------------
    static async login(employeeNumber, password, rememberMe = false) {
        const tokenData = await Auth.fetchTokenData(employeeNumber, password);
        const decodedToken = jwtDecode(tokenData.token);
        await Auth.setRememberMe(rememberMe);
        const fetchedUser = await Auth.fetchUser(decodedToken);
        const permissions = await Auth.getUserPermissionsData(decodedToken);
        const user = {
            ...fetchedUser,
            auth: tokenData,
            permissions,
        };
        await Auth.setUser(user);
        return user;
    }
    static async syncUser() {
        const user = await Auth.getUser();
        if (!user) {
            throw new Error("Can't sync user: no user cached in Auth");
        }
        const fetchedUser = await Auth.fetchUser(user.auth.token);
        const token = await Auth.getToken();
        const refreshToken = await Auth.getRefreshToken();
        if (!token || !refreshToken) {
            throw new Error("No token or refresh token present, can't refresh user.");
        }
        const permissions = await Auth.getUserPermissionsData(token);
        const authenticatedUser = {
            ...fetchedUser,
            auth: {
                token,
                refreshToken,
                needsToChangePassword: false, // We can't get this anywhere but login currently, and we shouldn't need to hit this between login and navigating to that page
            },
            permissions,
        };
        await Auth.setUser(authenticatedUser);
        return authenticatedUser;
    }
    static async logout() {
        await Auth.clear();
    }
    /** Used to reset password when user forgets and cannot log in
     * @param {string} employeeNumber - Included in the email/text link
     * @param {string} newPassword
     * @param {string} oneTimeToken - NOT an auth token, but a unique token sent with the email/text
     * @returns {Promise<void>}
     */
    static async resetPassword(employeeNumber, newPassword, oneTimeToken) {
        if (!isValidPassword(newPassword)) {
            throw new Error("Can't reset password: INVALID PASSWORD");
        }
        const { response } = await DB.POST('/users/reset-password', {
            body: {
                employeeNumber,
                token: oneTimeToken,
                newPassword,
            },
        });
        if (!response.ok) {
            throw new Error('Error resetting password: ' + response.statusText);
        }
    }
    /** Used to change the password when the user IS able to log in. Used when forced to change password upon login, and
     * in the profile dashboard
     * @param {string} currentPassword
     * @param {string} newPassword
     * @returns {Promise<Response>}
     */
    static async changePassword(currentPassword, newPassword) {
        if (!isValidPassword(newPassword)) {
            throw new Error("Can't reset password: INVALID PASSWORD");
        }
        const { response } = await DB.POST('/users/change-password', {
            body: {
                currentPassword,
                newPassword,
            },
        });
        if (!response.ok) {
            throw new Error('Error resetting password: ' + response.statusText);
        }
        const user = await Auth.getUser();
        if (user) {
            user.auth.needsToChangePassword = false;
            await Auth.setUser(user);
        }
        return response;
    }
    static async fetchUser(token) {
        if (!token || typeof token === 'string') {
            const decodedToken = await Auth.getDecodedToken(token);
            if (!decodedToken) {
                throw new Error("Can't fetch user: No token provided and no token found in cache");
            }
            token = decodedToken;
        }
        const tokenPayload = token;
        const { data: userData, error: getUserError } = await DB.GET('/users/{id}', {
            params: {
                path: {
                    id: tokenPayload.UserId,
                },
            },
        });
        if (getUserError) {
            throw new Error('Error fetching user: Invalid token or user not found');
        }
        return userData;
    }
    static async clear() {
        // clear the token and refresh token from memory
        await Auth.setToken(null);
        await Auth.setRefreshToken(null);
        await Auth.setUser(null);
        await Auth.setRememberMe(null);
    }
    static async refresh() {
        const refreshToken = await Auth.getRefreshToken();
        if (!refreshToken) {
            return null;
        }
        const { data: tokenData, error: authError } = await DB.POST('/auth/token/refresh', {
            body: {
                refreshToken,
            },
        });
        if (authError || !tokenData || !tokenData.token) {
            await Auth.logout();
            return null;
        }
        await Auth.setToken(tokenData.token);
        await Auth.setRefreshToken(tokenData.refreshToken);
        let user = await Auth.getUser();
        if (!user) {
            user = {
                ...(await Auth.fetchUser(tokenData.token)),
                auth: {
                    token: tokenData.token,
                    refreshToken: tokenData.refreshToken,
                    needsToChangePassword: false,
                },
                permissions: await Auth.getUserPermissionsData(tokenData.token),
            };
        }
        await Auth.setUser(user);
        return tokenData.token;
    }
    static async getAuthorizedFeatureCategories(user) {
        if (!user) {
            const cachedUser = await Auth.getUser();
            if (!cachedUser) {
                throw new Error('No user provided or found in cache');
            }
            user = cachedUser;
        }
        return Object.entries(featureCategoryScopes).reduce((acc, entry) => {
            const [category, categoryScopes] = entry;
            if (user?.permissions.scopes.some((scope) => categoryScopes.includes(scope))) {
                acc.push(category);
            }
            return acc;
        }, []);
    }
}
/**
 * Client API access interface
 *
 * This class is abstract and all members are static. It acts as state manager and function container for the API.
 * Before using any of the API functions, the `init` function must be called with a LocalStorageManager instance
 * (localStorage for web, AsyncStorage for React Native, etc.)
 *
 * The all-caps HTTP methods are the actual API functions, and are correctly typed based on the path provided using the
 * OpenAPI schema.
 */
class DB {
    static baseUrl = 'https://central-specialties-dev.azurewebsites.net/';
    static client = createClient({
        baseUrl: DB.baseUrl,
        fetch: DB.fetch,
    });
    // platform-specific function to store data in device storage
    static localStorage;
    static externalOnAuthStateChanged;
    static auth = Auth;
    static get isInitialized() {
        return !!DB.localStorage && DB.auth.isInitialized;
    }
    static onAuthStateChanged(token) {
        console.log('Adding authorization header to client');
        DB.client = createClient({
            baseUrl: DB.baseUrl,
            fetch: DB.fetch,
            headers: token
                ? {
                    Authorization: `Bearer ${token}`,
                }
                : {},
        });
        if (!!DB.externalOnAuthStateChanged) {
            DB.externalOnAuthStateChanged(token);
        }
    }
    static async init(storageManager, onAuthStateChanged) {
        DB.externalOnAuthStateChanged = onAuthStateChanged || null;
        DB.localStorage = storageManager;
        await Auth.init(DB.onAuthStateChanged);
    }
    static setBaseUrl(url) {
        DB.baseUrl = url;
        DB.client = createClient({
            baseUrl: DB.baseUrl,
            fetch: DB.fetch,
        });
    }
    static get GET() {
        return DB.client.GET;
    }
    static get POST() {
        return DB.client.POST;
    }
    static get PUT() {
        return DB.client.PUT;
    }
    static get PATCH() {
        return DB.client.PATCH;
    }
    static get DELETE() {
        return DB.client.DELETE;
    }
    static get HEAD() {
        return DB.client.HEAD;
    }
    static get OPTIONS() {
        return DB.client.OPTIONS;
    }
    // wrap fetch with custom internal error handling
    static async fetch(...args) {
        const response = await fetch(...args);
        // Handle specific error cases
        if (!response.ok) {
            // If unauthorized and not an auth request:
            if (response.status === 401 &&
                !args[0].includes('/auth/token')) {
                // if remember me is set, try to refresh the token, and logout only if it fails
                if (await Auth.getRememberMe()) {
                    const newToken = await DB.auth.refresh();
                    // If refresh worked, try adding adding the new token to headers (this was done inside refresh for future fetch calls, but not this current one)
                    if (!!newToken && args.length > 1 && 'headers' in args[1]) {
                        args[1].headers.set('Authorization', `Bearer ${newToken}`);
                    }
                    // Try the call again, and if it still doesn't work, log out
                    const response = await fetch(...args);
                    if (!response.ok && response.status === 401) {
                        await DB.auth.logout();
                    }
                    return response;
                }
                // if remember me is false, just log out
                else {
                    await DB.auth.logout();
                }
            }
        }
        return response;
    }
}

function filterObject(obj, keepKeys, invert) {
    return Object.fromEntries(Object.entries(obj).filter(([key, val]) => invert
        ? !keepKeys.includes(key)
        : keepKeys.includes(key)));
}

/**
 * Returns the value at the end of a given path in the data structure
 * @template T: the type of the root-level data objects
 * @template Path: the type of the path to be traversed
 * @param {T} obj - the root-level data object
 * @param {Path} path - an array of keys in sequential order, pointing to the
 *    property to be returned
 * @return {DeepDataValue<T, {path: Path}> | DeepDataValue<T, {path: Path}> | undefined} - the value at the end of the
 * 		path, or an array of values if there is an array somewhere in object that intersects with the path, or undefined
 * 		if the path is invalid for the given object
 */
const getDeepValue = (obj, path) => {
    if (typeof path === 'string') {
        path = path.split('.');
    }
    if (Array.isArray(path)) {
        // start with the full object
        let current = obj;
        // for each key in the path, assign current to the value at that key
        for (const [i, key] of path.entries()) {
            // If the current value is an array, return an array containing the values at the end of the path for each item in the array
            if (Array.isArray(current)) {
                return current.map((item) => getDeepValue(item, path.slice(i)));
            }
            // If path is invalid for the given object, return undefined
            if (current === null ||
                !(typeof current === 'object' && key in current)) {
                return undefined;
            }
            // Going deeper
            current = current[key];
        }
        // Once at the end of the path, return
        return current;
    }
    if (!obj) {
        throw new Error('Invalid object given to getDeepValue');
    }
    return obj[path];
};

const deepObjectValues = (obj) => {
    if (isPrimitive(obj)) {
        return [obj];
    }
    const values = [];
    Object.values(obj).forEach((value) => {
        if (typeof value === 'object') {
            values.push(...deepObjectValues(value));
        }
        else {
            values.push(value);
        }
    });
    return values;
};

/**
 * A generic class for working with arrays of data, particularly for nested data
 * structures containing objects and arrays. Allows for iteration and reduction
 * of deeply nested data with the ability to filter and group by any property.
 * @template T: the type of data to be stored in the ArrayDataUtility
 */
class ArrayDataUtility {
    data;
    constructor(data) {
        this.data = data;
    }
    /**
     * Checks if the given item matches the given set of constraints
     * TODO: Add option to check for partial matches (matches 'any' vs 'all')
     * @template K: the type of the item being checked.
     * @param {Filters<K> | null | undefined} constraints - an object containing
     *    key-value pairs to be matched against the item. If a constraint value is
     *    a primitive, it will return true if the item's value matches. If it's a
     *    function, it will return true if the function returns true when called.
     * @param {K} item - the item to be checked against the constraints
     * @param {boolean} shallow - if true, only check the top level of the item,
     *    otherwise, recursively check all levels specified in the constraints
     * @return {boolean} - true if `item` matches the constraints, false otherwise
     * @private
     */
    static matchesConstraints(constraints, item, shallow = false) {
        // if there are no constraints, item is good to go!
        if (!constraints)
            return true;
        if (typeof constraints === 'function')
            return constraints(item);
        // if the item is a primitive, check if it matches the constraints
        if (isPrimitive(item) || item === null || item === undefined) {
            if (isObject(constraints))
                return false;
            return item === constraints;
        }
        // for each item in the constraints object
        for (let [_key, searchVal] of Object.entries(constraints)) {
            const key = _key;
            // if searchVal is a function, call it with the item's value
            if (typeof searchVal === 'function' && !searchVal(item[key])) {
                return false;
            }
            // if searchVal is a primitive, check if it matches
            if (isPrimitive(searchVal) && item[key] !== searchVal) {
                return false;
            }
            // if searchVal is an object array, recursively check if it matches (or any of the items match in case of arrays)
            if (!shallow && isObject(searchVal)) {
                // if the item we're checking is an array, check that at least one of its items matches the current constraints
                if (Array.isArray(item[key]) &&
                    !item[key].some((v) => ArrayDataUtility.matchesConstraints(searchVal, v))) {
                    return false;
                }
                // if the item we're checking is itself an object, go a level deeper
                if (isObject(item[key]) &&
                    // NEW: add some janky patching to suppress new TypeScript errors
                    !ArrayDataUtility.matchesConstraints(searchVal, item[key])) {
                    return false;
                }
            }
        }
        return true;
    }
    /**
     * Wraps given data in a container object and tack on __GROUP_ID__ property
     * @template G: the type of the group identifier
     * @template T: the type of data to be stored in the ArrayDataUtility
     * @param {G} groupBy - a string or number or a function that returns a string
     *    or number indicating the group 'data' belongs to
     * @param {T} item - the data to be wrapped
     * @return {Grouped<G, T>} - an object with __GROUP_ID__ property and 'data'
     *    property containing 'item'
     * @private
     */
    static wrapInGroup(groupBy, item) {
        return {
            __GROUP_ID__: typeof groupBy === 'function'
                ? groupBy(item) // TODO: tests do not cover this case. There is no usage that passes a function as groupBy as of yet
                : groupBy,
            data: item,
        };
    }
    /**
     * Searches for the first item in the data array that matches the given set
     * of constraints
     * @param {Filters<T>} constraints - a DeepPartial of T, with desired values
     *    or functions that take the entire data object and return a boolean
     * @return {T | undefined} - the first item in the data array that matches
     *    the constraints, or undefined if no match is found
     */
    search(constraints) {
        return this.data.find((v) => ArrayDataUtility.matchesConstraints(constraints, v, false));
    }
    deepAllValues(obj) {
        const values = Object.values(obj);
        return values.reduce((acc, value) => {
            if (isObject(value)) {
                return [...acc, ...this.deepAllValues(value)];
            }
            return [...acc, value];
        }, []);
    }
    simpleKeywordSearch(searchString) {
        return this.data.filter((item) => {
            const allValues = this.deepAllValues(item);
            return allValues.some((value) => value.toString().toLowerCase().includes(searchString.toLowerCase()));
        });
    }
    filter(constraints, value) {
        // if constraints is a path, filter based on it and the value
        if (value && constraints !== undefined && Array.isArray(constraints)) {
            constraints = this.filterObjectFromPathAndValue(constraints, value);
        }
        return this.data.filter((v) => ArrayDataUtility.matchesConstraints(constraints, v, false));
    }
    filterObjectFromPathAndValue(path, value) {
        const filterObject = {};
        let currentFilterObject = filterObject;
        for (let i = 0; i < path.length - 1; i++) {
            // @ts-expect-error
            currentFilterObject[path[i]] = {};
            currentFilterObject = currentFilterObject[path[i]];
        }
        // @ts-expect-error
        currentFilterObject[path[path.length - 1]] = value;
        return filterObject;
    }
    getGroupIdentifierValue(item, groupBy) {
        // No grouping specifier:
        if (groupBy === undefined) {
            return undefined;
        }
        // No item to group:
        if (item === undefined || item === null) {
            return undefined;
        }
        // Grouping specifier is a function that returns the primitive identifier:
        if (typeof groupBy === 'function') {
            return groupBy(item);
        }
        // Grouping specifier is a deep path
        if (Array.isArray(groupBy) || typeof groupBy === 'string') {
            return getDeepValue(item, groupBy);
        }
        // // groupBy is a root-level key of the item
        // if (
        // 	isObject(item) &&
        // 	typeof groupBy === 'string'
        // ) {
        // 	return item[groupBy as keyof typeof item] as Identifier | undefined;
        // }
        return groupBy;
    }
    /**
     * Iterates over data at any specified depth/path, filtering and grouping as
     *   specified by the parameters
     * @template T: the type of the root-level data objects
     * @template Params: inferred from the parameters passed to the function,
     *    determines the return type of the generator
     * @param {DeepPath<T>} [path] - an array of progressive keys of data, indicating
     *    the path to the data to be iterated
     * @param {Filters<T>} [filters] - an DeepPartial of the data structure, with
     *    either specific values, or functions that take the enitre data structure
     *    and return a boolean (true if item should be included in the iteration)
     * @param {GroupingSpecifier<T>} [groupBy] - an identifier value or a function
     *    that takes T and returns one, indicating the group 'data' belongs to.
     *    identifier can be a string or number, or null to indicate that the item
     *    should not be grouped (acts as a filter)
     * @return {Generator<DeepDataValue<T, Params>>} - Data value, the type of
     *    which is determined by the 'path' and 'groupBy' params. If `path` is
     *    specified, the type will be that of the object nested in T, following
     *    each key in `path` sequentially (ignores containing arrays). If
     *    `groupBy` is specified, the type will be wrapped in a Grouped object,
     *    with the group identifier as the __GROUP_ID__ property, and the data
     *    value as the 'data' property
     * @throws {Error} - if the path is invalid (i.e. a key in the path does not
     *    exist in the data structure)
     */
    *iterate({ path, filters, groupBy } = {}) {
        // for each item at the base level of this data array:
        rootLevelDataIteration: for (const item of this.data) {
            // current item in the iteration
            let currentItem = item;
            // needed to allow possibility of nulls in the loop
            if (currentItem === undefined || currentItem === null) {
                return;
            }
            // filters at the outermost depth level
            let currentFilters = filters;
            // group identifier for this item, or to be passed down for any nested items if path goes deeper
            const groupIdentifier = this.getGroupIdentifierValue(item, groupBy);
            // if the current item doesn't match the filters skip it
            if (groupIdentifier === null || // null groupBy value acts as a filter
                !ArrayDataUtility.matchesConstraints(currentFilters, currentItem)) {
                continue rootLevelDataIteration;
            }
            // if there is a path, iterate through it. For each key and corresponding index:
            for (const [i, key] of path?.entries() || []) {
                // currentItem & filters go one level deeper, according to 'key'
                currentItem = currentItem?.[key];
                currentFilters = currentFilters?.[key];
                // if the current item is falsy, throw an error indicating that the path is invalid
                if (currentItem === undefined) {
                    yield currentItem;
                    continue rootLevelDataIteration;
                    // throw new Error(
                    // 	`Key ${key} not found in item. Check the path passed to iterate`
                    // );
                }
                // if the current item is an array, create a new ArrayDataUtility from it and defer iteration to it until exhausted, then continue to the next root-level item
                if (Array.isArray(currentItem)) {
                    const subArray = new ArrayDataUtility(currentItem);
                    yield* subArray.iterate({
                        path: path !== undefined
                            ? path.slice(i + 1)
                            : path,
                        filters: currentFilters,
                        groupBy: groupIdentifier,
                    });
                    continue rootLevelDataIteration;
                }
            }
            // BASE CASE: if the current item after all path traversal passes the filters, yield it (wrapped with groupIdentifier if specified)
            if (groupIdentifier === undefined) {
                yield currentItem;
            }
            else {
                yield ArrayDataUtility.wrapInGroup(groupIdentifier, currentItem);
            }
        }
    }
    /**
     * Apply a reducer function to each item in the data array, similar to
     * Array reduce, only with the ability to specify a path to iterate over,
     * and to filter and group the data
     * @template T: the type of the root-level data objects
     * @template Acc: the type of the accumulator
     * @template Params: inferred from the parameters passed to the function
     * @param {ReducerFunc<Acc, DeepDataValue<T, Params>>} reducer - a function
     *    that takes the accumulator and the current item, and returns the updated
     *    accumulator
     * @param {Acc} [initialValue=[]] - the initial value of the accumulator. Can
     *    be any type, but determines the type of Acc
     * @param {Params} [processingParams]  - an object containing any path,
     *    filters, or groupBy parameters to be passed to the iterate function
     * @return {Acc} - the final value of the accumulator
     */
    reduce(reducer, initialValue = [], processingParams) {
        let accumulator = initialValue;
        for (const item of this.iterate(processingParams)) {
            accumulator = reducer(accumulator, item);
        }
        return accumulator;
    }
    groupBy(groupBy) {
        return this.reduce((acc, { __GROUP_ID__, data }) => {
            if (__GROUP_ID__ === undefined) {
                return acc;
            }
            if (!acc[__GROUP_ID__]) {
                acc[__GROUP_ID__] = [];
            }
            acc[__GROUP_ID__].push(data);
            return acc;
        }, {}, { groupBy });
    }
}

function formatDurationHHmm(startTimestamp, endTimestamp) {
    const startDate = new Date(startTimestamp);
    const endDate = new Date(endTimestamp);
    let duration = endDate.getTime() - startDate.getTime();
    const hours = Math.floor(duration / (1000 * 60 * 60));
    duration -= hours * (1000 * 60 * 60);
    const minutes = Math.floor(duration / (1000 * 60));
    return `${padZero(hours)}:${padZero(minutes)}`;
}
function padZero(num) {
    return num < 10 ? `0${num}` : `${num}`;
}

/**
 * Represents a range in time, with a start and end date, and provides methods
 * for determining if, and where within, the timeframe a given date falls.
 */
class Timeframe {
    // Earliest & latest possible dates by default
    start = new Date(-8640000000000000);
    end = new Date(8640000000000000);
    /**
     * Creates a new Timeframe object
     * @param {DateRangeSpecifier} [start] - Start date, or array of start/end dates
     * @param {DateSpecifier} [end] - End date
     * @throws {Error} - If the end date is before the start date
     */
    constructor(start, end) {
        // Assign any start/end dates provided, converting if necessary
        if (start) {
            if (Array.isArray(start)) {
                [start, end] = start;
            }
            this.start = Timeframe.formatDate(start);
        }
        if (end) {
            this.end = Timeframe.formatDate(end);
        }
        // if the end date is before the start date, throw an error
        if (this.end < this.start) {
            throw new Error('End date must be after start date');
        }
    }
    /**
     * Converts a string to a Date object, or returns the given Date object
     * @param {Date | string} date - Date or string representing a date
     * @return {Date} - Date object representing the given date
     * @throws {Error} - If the given date is invalid
     * @private
     */
    static formatDate(date) {
        const formatted = typeof date === 'string' ? new Date(date) : date;
        if (formatted === null || isNaN(formatted.getTime())) {
            throw new Error('Invalid date passed to Timeframe.formatDate');
        }
        return formatted;
    }
    /**
     * Returns true if the given date is within the timeframe (start date
     * inclusive, end date exclusive)
     * @param {Date | string} date - Date or string representing a date
     * @throws {Error} - If the given date is invalid
     * @return {boolean} - True if the given date is within the timeframe
     * @throws {Error} - If the given date is invalid
     */
    includes(date) {
        const givenDate = Timeframe.formatDate(date);
        return givenDate >= this.start && givenDate < this.end;
    }
    /**
     * Returns the index of the group that the given date belongs to, or null if
     * the date is not within the timeframe
     * @param {DateSpecifier} date - Date or string representing a date
     * @param {GroupByTimeUnit} groupBy - The unit of time to group by (Year or Month)
     * @return {number | null} - The index of the group that the given date belongs
     *    to, or null if the date is not within the timeframe
     * @throws {Error} - If the given date is invalid
     */
    determineGroupIndex(date, groupBy = 'year') {
        const givenDate = Timeframe.formatDate(date);
        if (!this.includes(givenDate)) {
            return null;
        }
        switch (groupBy) {
            case 'year': {
                const yearNumber = givenDate.getUTCFullYear();
                const startYearNumber = this.start.getUTCFullYear();
                return yearNumber - startYearNumber;
            }
            case 'month': {
                const monthNumber = givenDate.getUTCMonth();
                const startMonthNumber = this.start.getUTCMonth();
                // account for months in any full-year difference between the two dates
                const fullYearDifferenceMonths = (this.determineGroupIndex(date, 'year') || 0) * 12;
                return fullYearDifferenceMonths + (monthNumber - startMonthNumber);
            }
            default: {
                throw Error(`Invalid groupBy unit '${groupBy}' passed to timeframe.determineGroupIndex.`);
            }
        }
    }
    /**
     * Returns the label for the group that the given date belongs to, or null if
     * the date is not within the timeframe
     * @param {DateSpecifier} date - Date or string representing a date to compare
     * @param {GroupByTimeUnit} groupBy - The unit of time to group by (Year or Month)
     * @return {string | null} - The label for the group that the given date belongs
     *    to, or null if the date is not within the timeframe
     * @throws {Error} - If the given date is invalid
     */
    determineGroupLabel(date, groupBy) {
        const index = this.determineGroupIndex(date, groupBy);
        if (index === null) {
            return null;
        }
        switch (groupBy) {
            case 'year': {
                const yearNumber = this.start.getUTCFullYear() + index;
                return yearNumber.toString();
            }
            case 'month': {
                const monthIndex = this.start.getUTCMonth() + index;
                const monthDate = new Date(this.start);
                monthDate.setUTCMonth(monthIndex);
                return monthDate.toLocaleString('default', { month: 'long' });
            }
        }
    }
}

const decimalHoursToTimeString = (decimalHours, padHours = true) => {
    // Separate the hours and the fractional hours
    const hours = Math.floor(decimalHours);
    const fractionalHours = decimalHours - hours;
    // Convert the fractional hours into minutes (round to nearest minute)
    const minutes = Math.round(fractionalHours * 60);
    // Format the hours and minutes into a hh:mm format
    const formattedHours = padHours
        ? hours.toString().padStart(2, '0')
        : hours.toString();
    const formattedMinutes = minutes.toString().padStart(2, '0');
    return `${formattedHours}:${formattedMinutes}`;
};

const hoursBetweenTimestamps = (startTimestamp, endTimestamp) => {
    const start = new Date(startTimestamp);
    const end = new Date(endTimestamp);
    const diff = end.getTime() - start.getTime();
    // return diff in decimal number of hours:
    return diff / (1000 * 60 * 60);
};

export { ArrayDataUtility, DB, Timeframe, allRoles, decimalHoursToTimeString, deepObjectValues, featureCategories, featureCategoryScopes, fieldEmployeeScopes, fieldSupervisorScopes, filterObject, formatDurationHHmm, getDeepValue, hoursBetweenTimestamps, isObject, isPrimitive, isValidEmail, isValidPassword, manageFeatureScopes, mechanicScopes, partsFeatureScopes, partsTeamScopes, payrollAdminScopes, repairAdminScopes, repairFeatureScopes, roleScopes, timeFeatureScopes, truckingAdminScopes, truckingFeatureScopes, usersFeatureScopes };
